/**
 * Provider Heatmap Component
 *
 * Leadership dashboard: provider pass/fail grid with animated rows.
 * Rows = providers, columns = measures, color-coded Badge cells.
 *
 * Phase 7: Inline expansion — clicking a row opens ClinicianExpansion
 * inside a SubGrid below the row (accordion: one at a time, toggle on re-click).
 *
 * Sticky columns: Provider, Type, Status — widths auto-fit to content, user-resizable.
 * Status has a right border to separate sticky from scrolling measure columns.
 */

import { type CSSProperties, Fragment, useCallback, useMemo, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  AnimatedTableWrapper,
  AnimatedTableBody,
  AnimatedTableRow,
} from '@/components/tailwindadmin/components/animated-component/AnimatedTable'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { SubGrid, SubGridContent } from '@/components/ui/sub-grid'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { ArrowUp, ArrowDown, ChevronsUpDown, ChevronRight, AlertCircle } from 'lucide-react'
import { formatPercentage } from '@/lib/equip-utils'
import type { LeadershipProviderRow } from '@/lib/equip-types'
import { MEASURE_DISPLAY_NAMES, type MeasureName } from '@/lib/equip-measure-config'
import {
  getActiveMeasureNames,
  getLeadershipMeasureColumnsFromSchedule,
  leadershipDisplayToBonusEligibleType,
  LEADERSHIP_MEASURE_ORDER,
} from '@/lib/measureScheduleSelectors'
import { useMeasureThresholdMap, type MeasureThresholdMap } from '@/hooks/equip/useMeasureThresholdMap'
import type { MeasureScheduleItem } from '@/lib/equip-types'
import { equipTable } from '@/views/equip/equip-table-styles'
import { ClinicianExpansion } from './ClinicianExpansion'

/** Build inline style for a sticky column: left offset + optional fixed width. */
function stickyStyle(left: number | string, fixedWidth: number | null): CSSProperties {
  if (fixedWidth == null) return { left }
  return { left, width: fixedWidth, minWidth: fixedWidth, maxWidth: fixedWidth }
}

/** Format a calculated award dollar amount for display in the heatmap cell.
 *  - null or 0 → null (caller renders a muted dash)
 *  - whole number → "$1,234"
 *  - fractional → "$1,234.56"
 */
function formatAward(value: number | null | undefined): string | null {
  if (value === null || value === undefined || value === 0) return null
  const isWhole = value % 1 === 0
  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: isWhole ? 0 : 2,
    maximumFractionDigits: isWhole ? 0 : 2,
  })}`
}

/**
 * Resolve the operational ACO gate threshold (0-1) from the config schedule.
 * The gate is a pass/fail eligibility decision, so it uses the operational
 * threshold (Q1 0.69, Q2-Q4 0.89) to match the calculation engine — not the
 * communicated display value. ACO_Visits has a single provider type, so a
 * type-agnostic lookup is exact; falls back to the prior constants if the
 * config row hasn't loaded.
 */
function acoGateThreshold(thresholdMap: MeasureThresholdMap, quarter: number): number {
  const item = thresholdMap.items.find(
    (i) => i.isActive && i.measureName === 'ACO_Visits'
  )
  return item ? item.threshold : quarter === 1 ? 0.69 : 0.89
}

/**
 * Returns true if the provider passes the ACO gate.
 * Only PC Physicians are subject to the ACO gate; all other types pass
 * unconditionally. Null performance (no eligible patients / N/A) is a pass.
 * The DB does NOT zero calculated_award for ACO-failing physicians — enforced
 * here in UI. ``acoGate`` is the operational ACO_Visits threshold for the
 * quarter, resolved by the caller from och.equip_config_measure_schedule.
 */
function passesAcoGate(provider: LeadershipProviderRow, acoGate: number): boolean {
  if (provider.bonusEligibleType !== 'PC Physician') return true
  const perf = provider.measures['ACO_Visits']?.performance
  if (perf === null || perf === undefined) return true
  return perf >= acoGate
}

/** White-with-border tooltip style matching chart tooltips. */
const TOOLTIP_CLASS = 'bg-popover text-popover-foreground border border-border shadow-sm rounded-lg text-xs'

/**
 * Returns true if the provider has at least one applicable measure
 * (per the schedule for their bonus_eligible_type × quarter) that is below
 * the pass threshold. Measures not active for the provider are ignored —
 * otherwise every row gets a false positive.
 *
 * The caller precomputes `activeByType` so we don't re-scan the schedule
 * per row. When the schedule hasn't loaded yet the type key is absent and
 * we fall back to checking every measure on the row (prior behavior).
 */
function needsCoaching(
  provider: LeadershipProviderRow,
  activeByType: Map<string, Set<string>>,
): boolean {
  const bet = provider.bonusEligibleType
    ? leadershipDisplayToBonusEligibleType(provider.bonusEligibleType)
    : null
  const active = bet ? activeByType.get(bet) : undefined
  return Object.entries(provider.measures).some(([name, m]) => {
    if (m === null || m === undefined) return false
    if (active && !active.has(name)) return false
    return !m.passed
  })
}

/**
 * Precompute the set of active grid-form measure names for every
 * `bonus_eligible_type` present in the provider list. Empty map when the
 * schedule hasn't loaded yet.
 */
function buildActiveByTypeMap(
  schedule: MeasureScheduleItem[],
  providers: LeadershipProviderRow[],
  quarter: number,
): Map<string, Set<string>> {
  const result = new Map<string, Set<string>>()
  if (schedule.length === 0) return result
  const rawTypes = new Set<string>()
  providers.forEach(p => {
    if (p.bonusEligibleType) rawTypes.add(p.bonusEligibleType)
  })
  rawTypes.forEach(raw => {
    const bet = leadershipDisplayToBonusEligibleType(raw)
    if (result.has(bet)) return
    const active = getActiveMeasureNames(schedule, bet, quarter)
    if (active) result.set(bet, active)
  })
  return result
}

// Sticky column classes — left offsets applied via inline style from stickyOffsets.
// Provider and Type share a base; Status adds a right border to separate from scrolling columns.
const STICKY_TD        = 'sticky z-20 bg-background'
const STICKY_TD_BORDER = 'sticky z-20 bg-background border-r border-border'
const STICKY_TH        = 'sticky z-20 bg-muted'
const STICKY_TH_BORDER = 'sticky z-20 bg-muted border-r border-border'

interface ProviderHeatmapProps {
  providers: LeadershipProviderRow[]
  isLoading: boolean
  availableQuarters?: string[]
  sortColumn: string
  onSortChange: (column: string) => void
  year: number
  quarter: number
  // Grid-level filter controls (quarter + provider type + care team + eligible toggle)
  selectedQuarter: string
  onQuarterChange: (value: string) => void
  selectedProviderType: string
  onProviderTypeChange: (value: string) => void
  selectedCareTeam: string
  onCareTeamChange: (value: string) => void
  eligibleOnly: boolean
  onEligibleOnlyChange: (value: boolean) => void
  activeOnly: boolean
  onActiveOnlyChange: (value: boolean) => void
  allProviders: LeadershipProviderRow[]
  onClearFilters: () => void
  canSeeAwards?: boolean
}

type SortDirection = 'asc' | 'desc'

export function ProviderHeatmap({
  providers,
  isLoading,
  availableQuarters,
  sortColumn,
  onSortChange,
  year,
  quarter,
  selectedQuarter,
  onQuarterChange,
  selectedProviderType,
  onProviderTypeChange,
  selectedCareTeam,
  onCareTeamChange,
  eligibleOnly,
  onEligibleOnlyChange,
  activeOnly,
  onActiveOnlyChange,
  allProviders,
  onClearFilters,
  canSeeAwards = true,
}: ProviderHeatmapProps) {
  // Derive unique provider types from the unfiltered provider list
  const uniqueProviderTypes = useMemo(() => {
    const types = new Set(
      allProviders
        .map((p) => p.bonusEligibleType)
        .filter((type): type is string => type !== null && type !== '')
    )
    return Array.from(types).sort()
  }, [allProviders])

  // Derive unique care teams from the unfiltered provider list
  const uniqueCareTeams = useMemo(() => {
    const teams = new Set(
      allProviders
        .map((p) => p.careTeam)
        .filter((team): team is string => team !== null && team !== '')
    )
    return Array.from(teams).sort()
  }, [allProviders])
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [expandedEmail, setExpandedEmail] = useState<string | null>(null)

  // Track visible width via CSS custom property so expansion rows can
  // size themselves to the viewport. Uses a callback ref — the early-return
  // loading skeleton means a plain useRef + useEffect([]) would miss the
  // element on first mount. Scroll-pinning is handled by CSS sticky on
  // the inner wrapper (enabled by clip-path instead of overflow-hidden on
  // the motion.div).
  const cleanupRef = useRef<(() => void) | null>(null)

  const scrollContainerElRef = useRef<HTMLDivElement | null>(null)
  const scrollContainerCallbackRef = useCallback((el: HTMLDivElement | null) => {
    cleanupRef.current?.()
    cleanupRef.current = null
    scrollContainerElRef.current = el
    if (!el) return

    const sync = () => el.style.setProperty('--container-w', `${el.clientWidth}px`)
    sync()

    const ro = new ResizeObserver(() => sync())
    ro.observe(el)
    cleanupRef.current = () => ro.disconnect()
  }, [])

  // ── Column width management ─────────────────────────────────
  // Sticky offsets are set via CSS custom properties on the scroll container
  // so that ResizeObserver updates never trigger React re-renders (which
  // caused Framer Motion row duplication when setState fired mid-animation).
  const providerThRef = useRef<HTMLTableCellElement | null>(null)
  const typeThRef = useRef<HTMLTableCellElement | null>(null)
  const statusThRef = useRef<HTMLTableCellElement>(null)
  const measuredWidthsRef = useRef({ provider: 220, type: 130 })

  // User resize overrides (null = natural auto-layout)
  const [userWidths, setUserWidths] = useState<Record<string, number | null>>({
    provider: null, type: null, status: null,
  })

  // Sync CSS custom properties on the scroll container from actual column widths.
  // No React state is set here — the browser applies the vars immediately.
  const stickyWidthCleanupRef = useRef<(() => void) | null>(null)
  const stickyWidthObserverRef = useCallback((node: HTMLTableCellElement | null) => {
    stickyWidthCleanupRef.current?.()
    stickyWidthCleanupRef.current = null
    providerThRef.current = node
    if (!node) return
    const sync = () => {
      const p = providerThRef.current
      const t = typeThRef.current
      const container = scrollContainerElRef.current
      if (!p || !t || !container) return
      const pW = p.offsetWidth
      const tW = t.offsetWidth
      measuredWidthsRef.current = { provider: pW, type: tW }
      container.style.setProperty('--sticky-type-left', `${pW}px`)
      container.style.setProperty('--sticky-status-left', `${pW + tW}px`)
    }
    sync()
    const ro = new ResizeObserver(sync)
    ro.observe(node)
    if (typeThRef.current) ro.observe(typeThRef.current)
    stickyWidthCleanupRef.current = () => ro.disconnect()
  }, [])

  // Sticky offsets: CSS vars when auto-layout, computed numbers when user has resized
  const stickyOffsets = useMemo(() => {
    if (userWidths.provider != null || userWidths.type != null) {
      const pW = userWidths.provider ?? measuredWidthsRef.current.provider
      const tW = userWidths.type ?? measuredWidthsRef.current.type
      return { provider: 0 as number | string, type: pW as number | string, status: (pW + tW) as number | string }
    }
    return {
      provider: 0 as number | string,
      type: 'var(--sticky-type-left, 220px)',
      status: 'var(--sticky-status-left, 350px)',
    }
  }, [userWidths])

  // Drag-to-resize column headers
  const handleResizeStart = useCallback((column: 'provider' | 'type' | 'status') => (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const refs = { provider: providerThRef, type: typeThRef, status: statusThRef }
    const startX = e.clientX
    const startWidth = refs[column].current?.offsetWidth ?? 150
    const onMouseMove = (ev: MouseEvent) => {
      setUserWidths(prev => ({ ...prev, [column]: Math.max(60, startWidth + (ev.clientX - startX)) }))
    }
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }, [])

  // Double-click resize handle → reset to natural auto-layout
  const handleResizeReset = useCallback((column: 'provider' | 'type' | 'status') => (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setUserWidths(prev => ({ ...prev, [column]: null }))
  }, [])

  // Accordion toggle: click same row to collapse, different row to swap
  const handleRowClick = (provider: LeadershipProviderRow) => {
    setExpandedEmail((prev) =>
      prev === provider.providerEmail ? null : provider.providerEmail
    )
  }

  const collapseExpansion = useCallback(() => {
    setExpandedEmail(null)
  }, [])

  // Load the measure schedule for this quarter and derive per-type active-measure sets.
  // The schedule is the single source of truth for which measures apply to
  // each bonus_eligible_type × quarter — replaces the hardcoded matrix.
  const thresholdMap = useMeasureThresholdMap(year, quarter)
  const activeByType = useMemo(
    () => buildActiveByTypeMap(thresholdMap.items, providers, quarter),
    [thresholdMap.items, providers, quarter],
  )
  const acoGate = useMemo(
    () => acoGateThreshold(thresholdMap, quarter),
    [thresholdMap, quarter],
  )

  // Compute quarter-relevant columns as union over all provider types present.
  // During the schedule's initial load, fall back to emitting columns for every
  // measure that actually has data — avoids flashing an empty grid.
  const measureNames = useMemo(() => {
    if (providers.length === 0) return []
    const fromSchedule = getLeadershipMeasureColumnsFromSchedule(
      thresholdMap.items,
      providers,
      quarter,
    )
    if (fromSchedule) return fromSchedule
    const present = new Set<string>()
    providers.forEach(p => {
      Object.keys(p.measures).forEach(name => present.add(name))
    })
    return LEADERSHIP_MEASURE_ORDER.filter(m => present.has(m))
  }, [providers, quarter, thresholdMap.items])

  // Sort providers
  const sortedProviders = useMemo(() => {
    if (!sortColumn) return providers

    const sorted = [...providers].sort((a, b) => {
      let aValue: number | string | null
      let bValue: number | string | null

      if (sortColumn === 'providerName') {
        aValue = a.providerName
        bValue = b.providerName
      } else if (sortColumn === 'totalPercentage') {
        aValue = a.totalPercentage ?? -1
        bValue = b.totalPercentage ?? -1
      } else if (sortColumn === 'calculatedAward') {
        aValue = a.calculatedAward ?? -1
        bValue = b.calculatedAward ?? -1
      } else if ((measureNames as string[]).includes(sortColumn)) {
        // Sorting by measure column
        aValue = a.measures[sortColumn]?.performance ?? -1
        bValue = b.measures[sortColumn]?.performance ?? -1
      } else {
        return 0
      }

      // String comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue)
      }

      // Numeric comparison
      const aNum = typeof aValue === 'number' ? aValue : -1
      const bNum = typeof bValue === 'number' ? bValue : -1
      return sortDirection === 'asc' ? aNum - bNum : bNum - aNum
    })

    return sorted
  }, [providers, sortColumn, sortDirection, measureNames])

  // Handle sort click
  const handleSortClick = (column: string) => {
    if (sortColumn === column) {
      // Toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // New column, default to desc
      onSortChange(column)
      setSortDirection('desc')
    }
  }

  // Sort icon component
  const SortIcon = ({ column }: { column: string }) => {
    if (sortColumn !== column) {
      return <ChevronsUpDown className="inline-block ml-1 h-4 w-4 text-muted-foreground" />
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="inline-block ml-1 h-4 w-4" />
    ) : (
      <ArrowDown className="inline-block ml-1 h-4 w-4" />
    )
  }

  // Total colSpan for expansion row: provider + type + status + measures + (award if visible)
  const totalColumns = measureNames.length + 3 + (canSeeAwards ? 1 : 0)

  if (isLoading) {
    return <Skeleton className="h-[400px] w-full rounded-[var(--radius)]" />
  }

  if (providers.length === 0) {
    return (
      <Card className="p-6">
        <p className="text-center text-muted-foreground">No clinician data available</p>
      </Card>
    )
  }

  return (
    <Card>
      {/* Compact filter row — quarter, provider type, eligible toggle */}
      <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-border overflow-x-auto">
        {/* Quarter selector */}
        <Select value={selectedQuarter} onValueChange={onQuarterChange}>
          <SelectTrigger className="h-8 w-[130px] text-xs flex-shrink-0">
            <SelectValue placeholder="Quarter" />
          </SelectTrigger>
          <SelectContent>
            {(availableQuarters ?? []).map((q) => (
              <SelectItem key={q} value={q}>
                {q}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Provider Type selector */}
        <Select value={selectedProviderType} onValueChange={onProviderTypeChange}>
          <SelectTrigger className="h-8 w-[190px] text-xs flex-shrink-0">
            <SelectValue placeholder="All Clinician Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Clinician Types</SelectItem>
            {uniqueProviderTypes.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Care Team selector — only shown when care teams exist */}
        {uniqueCareTeams.length > 0 && (
          <Select value={selectedCareTeam} onValueChange={onCareTeamChange}>
            <SelectTrigger className="h-8 w-[170px] text-xs flex-shrink-0">
              <SelectValue placeholder="All Care Teams" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Care Teams</SelectItem>
              {uniqueCareTeams.map((team) => (
                <SelectItem key={team} value={team}>
                  {team}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Eligible toggle — inline after dropdowns */}
        <Switch
          id="heatmap-eligible-only"
          checked={eligibleOnly}
          onCheckedChange={onEligibleOnlyChange}
          className="scale-90 flex-shrink-0"
        />
        <Label
          htmlFor="heatmap-eligible-only"
          className="text-xs cursor-pointer whitespace-nowrap leading-normal text-muted-foreground flex-shrink-0 mb-0"
        >
          Eligible only
        </Label>
        {/* Active only toggle — hides terminated-in-quarter providers */}
        <Switch
          id="heatmap-active-only"
          checked={activeOnly}
          onCheckedChange={onActiveOnlyChange}
          className="scale-90 flex-shrink-0"
        />
        <Label
          htmlFor="heatmap-active-only"
          className="text-xs cursor-pointer whitespace-nowrap leading-normal text-muted-foreground flex-shrink-0 mb-0"
        >
          Active only
        </Label>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearFilters}
          className="h-5 px-2 text-xs whitespace-nowrap flex-shrink-0"
        >
          Clear
        </Button>
      </div>

      <div className={equipTable.wrapper}>
      <div className="overflow-x-auto" ref={scrollContainerCallbackRef}>
        <AnimatedTableWrapper><table className={equipTable.table}>
          <thead className={equipTable.thead}>
            <tr className={equipTable.theadTr}>
              {/* Sticky: Provider */}
              <th
                ref={stickyWidthObserverRef}
                className={cn(equipTable.th, STICKY_TH, 'cursor-pointer hover:bg-muted/80')}
                style={stickyStyle(stickyOffsets.provider, userWidths.provider)}
                onClick={() => handleSortClick('providerName')}
              >
                Provider
                <SortIcon column="providerName" />
                <div
                  className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-primary/30"
                  onMouseDown={handleResizeStart('provider')}
                  onDoubleClick={handleResizeReset('provider')}
                  onClick={(e) => e.stopPropagation()}
                />
              </th>
              {/* Sticky: Type */}
              <th
                ref={typeThRef}
                className={cn(equipTable.th, STICKY_TH)}
                style={stickyStyle(stickyOffsets.type, userWidths.type)}
              >
                Type
                <div
                  className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-primary/30"
                  onMouseDown={handleResizeStart('type')}
                  onDoubleClick={handleResizeReset('type')}
                  onClick={(e) => e.stopPropagation()}
                />
              </th>
              {/* Sticky: Status — right border separates sticky from scrolling */}
              <th
                ref={statusThRef}
                className={cn(equipTable.th, STICKY_TH_BORDER)}
                style={stickyStyle(stickyOffsets.status, userWidths.status)}
              >
                Status
                <div
                  className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-primary/30"
                  onMouseDown={handleResizeStart('status')}
                  onDoubleClick={handleResizeReset('status')}
                  onClick={(e) => e.stopPropagation()}
                />
              </th>
              {/* Scrolling: measure columns */}
              {measureNames.map((m) => (
                <th
                  key={m}
                  className={cn(equipTable.th, 'text-center cursor-pointer hover:bg-muted/50')}
                  onClick={() => handleSortClick(m)}
                >
                  {MEASURE_DISPLAY_NAMES[m as MeasureName] ?? m}
                  <SortIcon column={m} />
                </th>
              ))}
              {/* Scrolling: Award (hidden when canSeeAwards is false) */}
              {canSeeAwards && (
                <th
                  className={cn(equipTable.th, 'cursor-pointer hover:bg-muted/50')}
                  onClick={() => handleSortClick('calculatedAward')}
                >
                  Award
                  <SortIcon column="calculatedAward" />
                </th>
              )}
            </tr>
          </thead>
          <AnimatedTableBody>
            {sortedProviders.map((provider, index) => {
              const isExpanded = expandedEmail === provider.providerEmail
              return (
                <Fragment key={`${provider.providerEmail}-${provider.npi}-${provider.bonusEligibleType ?? index}`}>
                  <AnimatedTableRow
                    index={index}
                    className={cn(
                      equipTable.trClickable,
                      isExpanded && 'bg-muted border-l-2 border-l-primary',
                      provider.isInactiveInQuarter && 'opacity-50'
                    )}
                    onClick={() => handleRowClick(provider)}
                  >
                    {/* Sticky: Provider cell */}
                    <td
                      className={cn(equipTable.td, STICKY_TD, 'font-medium',
                        isExpanded && 'bg-muted'
                      )}
                      style={stickyStyle(stickyOffsets.provider, userWidths.provider)}
                    >
                      <div className="flex items-center gap-1.5 flex-nowrap overflow-hidden">
                        <ChevronRight
                          className={cn(
                            'h-4 w-4 shrink-0 transition-transform text-muted-foreground',
                            isExpanded && 'rotate-90 text-primary'
                          )}
                        />
                        <span className="truncate min-w-0">{provider.providerName}</span>
                        {/* Coaching indicator: warning icon when provider is below threshold on any measure */}
                        {needsCoaching(provider, activeByType) && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <AlertCircle className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                              </TooltipTrigger>
                              <TooltipContent className={TOOLTIP_CLASS}>
                                Below 90% on one or more measures — coaching opportunity
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {(() => {
                          // Both flags must be true for "Eligible".
                          // eligible_at_quarter_end may be NULL (not yet set) for the
                          // current quarter — treat NULL/false as ineligible per #86.
                          const eligible =
                            provider.eligibleAtQuarterStart && provider.eligibleAtQuarterEnd
                          return (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span
                                    className={
                                      eligible
                                        ? 'inline-flex shrink-0 px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-success/15 text-success uppercase tracking-wider'
                                        : 'inline-flex shrink-0 px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-destructive/15 text-destructive uppercase tracking-wider'
                                    }
                                  >
                                    {eligible ? 'Eligible' : 'Ineligible'}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent className={TOOLTIP_CLASS}>
                                  {eligible
                                    ? 'This clinician met productivity requirements at both quarter start and quarter end'
                                    : 'This clinician did not meet productivity requirements at quarter start and/or quarter end'}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )
                        })()}
                      </div>
                    </td>
                    {/* Sticky: Type cell */}
                    <td
                      className={cn(equipTable.td, STICKY_TD, 'text-muted-foreground',
                        isExpanded && 'bg-muted'
                      )}
                      style={stickyStyle(stickyOffsets.type, userWidths.type)}
                    >
                      {provider.bonusEligibleType}
                    </td>
                    {/* Sticky: Status cell — right border */}
                    <td
                      className={cn(equipTable.td, STICKY_TD_BORDER, 'text-muted-foreground',
                        isExpanded && 'bg-muted'
                      )}
                      style={stickyStyle(stickyOffsets.status, userWidths.status)}
                    >
                      {provider.isInactiveInQuarter && provider.eventusStatus?.toLowerCase() === 'leave of absence' ? (
                        <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-500/15 text-blue-600 dark:text-blue-400 uppercase tracking-wide">
                          LOA
                        </span>
                      ) : provider.isInactiveInQuarter ? (
                        <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-500/15 text-red-600 dark:text-red-400 uppercase tracking-wide">
                          Terminated
                        </span>
                      ) : provider.employmentStatus === 'Part Time' ? (
                        <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-500/15 text-amber-600 dark:text-amber-400 uppercase tracking-wide">
                          {provider.avgDaysPerWeek != null ? `${provider.avgDaysPerWeek.toFixed(1)}d/wk` : 'PT'}
                        </span>
                      ) : (
                        <span>Full Time</span>
                      )}
                    </td>
                    {/* Scrolling: measure cells */}
                    {measureNames.map((m) => {
                      // Case 1: Measure doesn't apply to this provider's type/quarter.
                      // Active-set is precomputed per type; absent key = schedule
                      // hasn't loaded yet → don't render the placeholder to avoid
                      // flashing "—" before data arrives.
                      const bet = provider.bonusEligibleType
                        ? leadershipDisplayToBonusEligibleType(provider.bonusEligibleType)
                        : null
                      const active = bet ? activeByType.get(bet) : undefined
                      if (active && !active.has(m)) {
                        return (
                          <td key={m} className={`${equipTable.td} text-center`}>
                            <span className="text-muted-foreground select-none">—</span>
                          </td>
                        )
                      }
                      // Case 2: Applicable but no data yet
                      const measure = provider.measures[m]
                      if (!measure) {
                        return (
                          <td key={m} className={`${equipTable.td} text-center`}>
                            <span className="text-muted-foreground">--</span>
                          </td>
                        )
                      }
                      // Case 3: Has data — render colored badge
                      return (
                        <td
                          key={m}
                          className={cn(
                            equipTable.td,
                            'text-center',
                            measure.passed ? 'bg-lightsuccess/50' : 'bg-lighterror/50'
                          )}
                        >
                          <Badge variant={measure.passed ? 'lightSuccess' : 'lightError'}>
                            {formatPercentage(measure.performance)}
                          </Badge>
                        </td>
                      )
                    })}
                    {/* Scrolling: Award cell — hidden when canSeeAwards is false */}
                    {canSeeAwards && (
                      <td className={`${equipTable.td} tabular-nums`}>
                        {(() => {
                          // Consistent with badge: both flags must be true (#86)
                          const eligible =
                            provider.eligibleAtQuarterStart && provider.eligibleAtQuarterEnd
                          if (!eligible) {
                            return <span className="text-muted-foreground select-none">—</span>
                          }
                          if (!passesAcoGate(provider, acoGate)) {
                            return <span className="text-muted-foreground select-none">—</span>
                          }
                          const award = formatAward(provider.calculatedAward)
                          return award ? (
                            <span className="font-medium text-foreground">{award}</span>
                          ) : (
                            <span className="text-muted-foreground select-none">—</span>
                          )
                        })()}
                      </td>
                    )}
                  </AnimatedTableRow>

                  {/* Inline expansion row (LEAD-01, SGRD-04) */}
                  <AnimatePresence initial={false}>
                    {isExpanded && (
                      <tr>
                        <td colSpan={totalColumns} className="p-0 border-0">
                          <motion.div
                            key={`expansion-${provider.providerEmail}`}
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{
                              height: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] },
                              opacity: { duration: 0.2, delay: 0.05 },
                            }}
                            style={{ clipPath: 'inset(0)' }}
                          >
                            <div
                              className="sticky left-0"
                              style={{ width: 'var(--container-w)' }}
                            >
                              <SubGrid isExpanded={true} depth={1}>
                                <SubGridContent>
                                  <ClinicianExpansion
                                    providerEmail={provider.providerEmail}
                                    providerName={provider.providerName}
                                    providerNpi={provider.npi}
                                    calculatedAward={provider.calculatedAward}
                                    year={year}
                                    quarter={quarter}
                                    onCollapse={collapseExpansion}
                                    canSeeAwards={canSeeAwards}
                                  />
                                </SubGridContent>
                              </SubGrid>
                            </div>
                          </motion.div>
                        </td>
                      </tr>
                    )}
                  </AnimatePresence>
                </Fragment>
              )
            })}
          </AnimatedTableBody>
        </table></AnimatedTableWrapper>
      </div>
      </div>
    </Card>
  )
}
